import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import PageHeader from '@features/PageHeader';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import { ModalConfirm } from '@components/Modal';
import { useTranslation } from '@hooks';
import { accountActions, alertActions, labelsActions, surveysActions, locationsProfilesActions } from '@actions';
import { surveyService } from '@services';
import { handleApiError } from '@helpers/api';
import { VIEW_TYPE } from '@constants/surveys';
import { ROLE } from '@constants/user';
import { SurveysIcon } from 'assets/images/icons';
import TemplateItem from './TemplateItem';
import EditModal from './EditModal';

const Container = styled('div')({
    '@media (max-width: 1024px)': {
        padding: 16,
        height: 'calc(var(--100vh) - 108px)',
        overflowY: 'auto',
    },
});

const CardsGrid = styled('div')({
    display: 'grid',
    gridGap: 16,
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    '@media (max-width: 480px)': {
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    },
});

const SurveysPage = () => {
    const { t } = useTranslation();

    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedSurvey, setSelectedSurvey] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modal, setModal] = useState({ open: false, type: VIEW_TYPE.EDIT });

    const user = useSelector(state => state.auth.user);
    const account = useSelector(state => state.account?.account);
    const data = useSelector(state => state.surveys.data);
    const brands = useSelector(state => state.brands.data);
    const surveysTotalCount = useSelector(state => state.surveys.totalCount);
    const dispatch = useDispatch();

    const isUser = user.role === ROLE.USER;

    useEffect(() => {
        dispatch(surveysActions.getAll());
        dispatch(labelsActions.get());
    }, []);

    const onCloseEditModal = () => {
        // INFO: reset locations profiles to prevent trigger useEffect on availablePlatforms
        dispatch(locationsProfilesActions.reset());
        setModal({ open: false, type: VIEW_TYPE.EDIT });
        setSelectedSurvey(null);
    };

    const onCreateEmptySurvey = async () => {
        setIsSubmitting(true);
        try {
            const newSurvey = await surveyService.createEmptySurvey();
            await dispatch(surveysActions.getAll());
            await dispatch(accountActions.get());
            setSelectedSurvey(newSurvey);
            setModal({ open: true, type: VIEW_TYPE.EDIT });
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.createFail')));
        } finally {
            setIsSubmitting(false);
        }
    };

    const onDelete = async () => {
        try {
            await surveyService.deleteSurvey(selectedSurvey.id);
            await dispatch(surveysActions.getAll());
            await dispatch(accountActions.get());
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            setDeleteModalOpen(false);
            setSelectedSurvey(null);
            if (modal.open) {
                setModal({ open: false, type: VIEW_TYPE.EDIT });
            }
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail', { amount: error.count });
        }
    };

    return (
        <Container>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<SurveysIcon />}
                title={t('FeedbackSurveys.title')}
                subtitle={t('FeedbackSurveys.subtitle')}
                metrics={
                    !isUser && (
                        <Typography variant="body1" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                            {t('FeedbackSurveys.created')} {surveysTotalCount}
                        </Typography>
                    )
                }
            >
                {!isUser && (
                    <Button variant="contained" onClick={onCreateEmptySurvey} disabled={isSubmitting}>
                        {t('buttons.addSurvey')}
                    </Button>
                )}
            </PageHeader>
            <CardsGrid>
                {data.map(item => (
                    <TemplateItem
                        key={item.survey.id}
                        data={item}
                        companies={account?.companies}
                        brands={brands}
                        isSubmitting={isSubmitting}
                        onClickEdit={() => {
                            setSelectedSurvey(item.survey);
                            setModal({ open: true, type: VIEW_TYPE.EDIT });
                        }}
                        onClickView={() => {
                            setSelectedSurvey(item.survey);
                            setModal({ open: true, type: VIEW_TYPE.VIEW });
                        }}
                        onClickDelete={() => {
                            setSelectedSurvey(item.survey);
                            setDeleteModalOpen(true);
                        }}
                    />
                ))}
            </CardsGrid>
            {modal.open ? (
                <EditModal
                    modal={modal}
                    onClose={onCloseEditModal}
                    account={account}
                    selectedSurvey={selectedSurvey}
                    setSelectedSurvey={setSelectedSurvey}
                    onDelete={() => setDeleteModalOpen(true)}
                    brands={brands}
                />
            ) : null}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </Container>
    );
};

export default SurveysPage;
